import React, { useState } from 'react';
import classnames from 'classnames/bind';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { usePasswordReset } from 'src/SERVICES/api/auth.api';
import { notify } from '@NOTIFICATION/Notificator';

import EmailInput from '@PAGES/common/login/components/EmailInput';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';

import styles from './PasswordResetForm.module.scss';

const cx: CX = classnames.bind(styles);

const schema = yup.object({
    email: yup.string().trim().email('Email is not valid').required('Email is required'),
});

type Schema = yup.InferType<typeof schema>;

interface Props {
    onBack: () => void;
}

function PasswordResetForm({ onBack }: Props) {
    const [resetPassword, { isLoading }] = usePasswordReset();
    const [isSuccess, setIsSuccess] = useState(false);

    const { handleSubmit, control } = useForm<Schema>({
        defaultValues: {
            email: '',
        },
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const {
        field: email,
        fieldState: { error: emailError },
    } = useController({
        name: 'email',
        control,
    });

    async function onValid(values: Schema) {
        try {
            await resetPassword({ email: values.email }).unwrap();
            setIsSuccess(true);
            notify.success('Password reset instructions sent to your email', {
                toastId: 'password-reset-success',
            });
        } catch (error) {
            notify.error('Failed to send password reset email', {
                toastId: 'password-reset-error',
            });
        }
    }

    function onInvalid() {
        // Handle form validation errors
    }

    if (isSuccess) {
        return (
            <div className={cx('password-reset-form')}>
                <div className={cx('content')}>
                    <div className={cx('title')}>
                        Check Your Email
                    </div>
                    <div className={cx('success-message')}>
                        A new password has been sent to your email address.
                        Please check your inbox and use the new password to sign in.
                    </div>
                    <div className={cx('buttons')}>
                        <button
                            type="button"
                            className={cx('back-button')}
                            onClick={onBack}
                        >
                            Back to Sign In
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cx('password-reset-form')}>
            <div className={cx('content')}>
                <div className={cx('title')}>
                    Reset Password
                </div>
                <div className={cx('description')}>
                    Enter your email address and we&apos;ll send you a new password.
                </div>
                <form
                    onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault();
                        if (!isLoading) {
                            handleSubmit(onValid, onInvalid)(event);
                        }
                    }}
                >
                    <div className={cx('input-wrapper')}>
                        <EmailInput
                            value={email.value}
                            type={emailError ? 'error' : 'default'}
                            onChange={(val: string) => {
                                email.onChange(val);
                            }}
                        />
                        <div className={cx('error-message')}>
                            {emailError?.message ? emailError.message : ''}
                        </div>
                    </div>
                    <HiddenFormButton />
                </form>

                <div className={cx('buttons')}>
                    <button
                        type="submit"
                        className={cx('submit-button', { processing: isLoading })}
                        disabled={isLoading}
                        onClick={() => {
                            handleSubmit(onValid, onInvalid)();
                        }}
                    >
                        {isLoading ? 'Sending...' : 'Send New Password'}
                    </button>
                    <button
                        type="button"
                        className={cx('back-button')}
                        onClick={onBack}
                    >
                        Back to Sign In
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PasswordResetForm; 